import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { constructSignupPayload } from '../utils/PayloadUtils';
import { getSignupUrl } from '../utils/UrlUtilities';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getUserData } from '../utils/TokenUtilities';
import { useUser } from '../contexts/UserContext';
import GoogleAuthButton from './GoogleAuthButton';

export default function SignUp() {
    const { signupState } = useUser();
    const [searchParams] = useSearchParams();
    const referralToken = searchParams.get('ref') || '';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('+88');
    const [showPasswordRules, setShowPasswordRules] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const isEmailValid = email => /^\S+@\S+\.\S+$/.test(email);
    const isPasswordValid = password =>
    new RegExp(`^(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};:'",.<>/?]).{8,}$`).test(password);


    const isPhoneValid = phone => phone === '+88' || /^\+88\d{11}$/.test(phone);

    const isFormValid = () =>
        name.trim() &&
        isEmailValid(email) &&
        isPasswordValid(password) &&
        password === confirmPassword &&
        isPhoneValid(phone);

    const CallApi = useApiCaller();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid()) {
            return;
        }
        const signupPayload = constructSignupPayload(name, email, password, phone, referralToken);
        const signupUrl = getSignupUrl();

        const signupResponse = await CallApi({
            url: signupUrl,
            method: 'POST',
            payload: signupPayload,
            token: false,
        });

        if (signupResponse.success) {
            const userData = getUserData(signupResponse.data.jwt_token);
            signupState({
                userId: userData?.user_id,
                email: userData?.user_email,
                name: userData?.user_name,
                user_role: userData?.user_role,
                jwt_token: signupResponse.data.jwt_token,
                refresh_token: signupResponse.data.refresh_token
            });
            navigate('/home');
        } else {
            console.error('Signup Error Message::', signupResponse.message);
        }
    };

    const handlePhoneChange = e => {
        const raw = e.target.value;
        if (!raw.startsWith('+88')) return;
        setPhone(raw);
    };

    return (
        <div className="container d-flex justify-content-center align-items-start">
            <div className="card shadow-sm my-5" style={{ maxWidth: 480, width: '100%', border: '1px solid #ede9fe', borderRadius: '0.9rem', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', padding: '1.4rem 1.25rem', textAlign: 'center' }}>
                    <h3 className="mb-1 fw-bold">Create your account</h3>
                    <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>Join Kobra Prep — it's free to start</div>
                </div>
                <div className="card-body p-4">
                {referralToken && (
                    <div className="alert alert-success py-2 small">
                        🎁 You were invited by a friend! Sign up and <strong>verify your email</strong> to
                        unlock a discount on your first plan.
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Full Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={name}
                        onChange={(e) => setName(e.target.value)} required />
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Email address <span className="text-danger">*</span></label>
                    <input
                        type="email"
                        className={`form-control ${email && !isEmailValid(email) ? 'is-invalid' : isEmailValid(email) ? 'is-valid' : ''}`}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    {!isEmailValid(email) && email && (
                        <div className="invalid-feedback">Enter a valid email.</div>
                    )}
                    </div>

                    <div className="mb-3 position-relative">
                    <label className="form-label fw-semibold" style={{ color: '#5b21b6' }}>
                        Password <span className="text-danger">*</span>{' '}
                        <span
                            className="text-primary"
                            style={{ cursor: 'pointer', fontWeight: 400 }}
                            onClick={() => setShowPasswordRules(!showPasswordRules)}
                            title="Click to toggle password rules"
                            >
                            Password Set Rule
                        </span>
                    </label>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control ${password && !isPasswordValid(password) ? 'is-invalid' : ''}`}
                        style={{ paddingRight: '2.5rem' }}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        title={showPassword ? 'Hide password' : 'Show password'}
                        style={{ position: 'absolute', right: '0.6rem', top: '2.15rem', background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', padding: 0, zIndex: 3 }}
                    >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    {showPasswordRules && (
                        <div className="alert alert-info mt-2 p-2">
                            Password must be at least 8 characters long and contain at least one uppercase letter, one number, and one special character.<br />
                            <strong>Allowed special characters:</strong> ! @ # $ % ^ &amp; * ( ) _ + - = [ ] {'{'} {'}'} ; : ' " , . &lt; &gt; / ?
                        </div>
                    )}
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Confirm Password <span className="text-danger">*</span></label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            className={`form-control ${
                            confirmPassword
                                ? password === confirmPassword
                                ? 'is-valid'
                                : 'is-invalid'
                                : ''
                            }`}
                            style={{ paddingRight: '2.5rem' }}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword((s) => !s)}
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                            title={showConfirmPassword ? 'Hide password' : 'Show password'}
                            style={{ position: 'absolute', right: '0.6rem', top: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', padding: 0, zIndex: 3 }}
                        >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                        <div className="invalid-feedback d-block">Passwords do not match.</div>
                    )}
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Phone Number <small className="text-muted fw-normal">(optional)</small></label>
                    <input
                        type="tel"
                        className={`form-control ${phone.length > 3 && !isPhoneValid(phone) ? 'is-invalid' : phone.length > 3 && isPhoneValid(phone) ? 'is-valid' : ''}`}
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder="+8801XXXXXXXXX"
                    />
                    {phone.length > 3 && !isPhoneValid(phone) && (
                        <div className="invalid-feedback">Phone must start with +88 and you have to put another 11 digits.</div>
                    )}
                    </div>

                    <button
                        type="submit"
                        className="btn w-100 text-white fw-semibold"
                        style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', border: 'none', boxShadow: '0 3px 0 #4c1d95, 0 4px 6px rgba(0,0,0,0.2)' }}
                        disabled={!isFormValid()}
                    >
                    Sign Up
                    </button>
                </form>

                <div className="d-flex align-items-center text-muted my-3">
                    <hr className="flex-grow-1" /><span className="px-2 small">or</span><hr className="flex-grow-1" />
                </div>
                <GoogleAuthButton onAuthed={signupState} referralToken={referralToken} />

                <div className="text-center mt-3 small text-muted">
                    Already have an account? <Link to="/login" style={{ color: '#7c3aed', fontWeight: 600 }}>Log in</Link>
                </div>
                </div>
            </div>
        </div>
    );
}
